import { Box, Typography, IconButton, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ImportJob } from './types';
import { ImportJobCard } from './ImportJobCard';
import { useState } from 'react';

interface ImportJobsListProps {
    jobs: ImportJob[];
    loading: boolean;
    onCancelJob: (jobId: string) => void;
    policySetMap?: Record<string, string>;
}

export function ImportJobsList({ jobs, loading, onCancelJob, policySetMap }: ImportJobsListProps) {
    const [activeExpanded, setActiveExpanded] = useState(true);
    const [needsAttentionExpanded, setNeedsAttentionExpanded] = useState(true);
    const [recentExpanded, setRecentExpanded] = useState(true);

    // Group jobs by status and sort
    const activeJobs = jobs
        .filter(job => job.status === 'QUEUED' || job.status === 'IN_PROGRESS')
        .sort((a, b) => {
            // IN_PROGRESS jobs first, then QUEUED
            if (a.status === 'IN_PROGRESS' && b.status === 'QUEUED') return -1;
            if (a.status === 'QUEUED' && b.status === 'IN_PROGRESS') return 1;

            // Within IN_PROGRESS, sort by percent complete (descending - highest first)
            if (a.status === 'IN_PROGRESS' && b.status === 'IN_PROGRESS') {
                const percentA = a.progressPercentage || 0;
                const percentB = b.progressPercentage || 0;
                return percentB - percentA;
            }

            // QUEUED jobs maintain their original order
            return 0;
        });
    const failedJobs = jobs.filter(job => job.status === 'FAILED');
    const completedJobs = jobs.filter(job => job.status === 'COMPLETED');

    // Filter recent completed jobs (last 24 hours)
    const now = Date.now();
    const recentCompletedJobs = completedJobs.filter(job => {
        const completedTime = new Date(job.completedTs || job.updatedTs).getTime();
        const hoursSinceCompletion = (now - completedTime) / (1000 * 60 * 60);
        return hoursSinceCompletion < 24;
    });

    const hasActiveJobs = activeJobs.length > 0;
    const hasFailedJobs = failedJobs.length > 0;
    const hasRecentJobs = recentCompletedJobs.length > 0;

    // Empty state
    if (!loading && jobs.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '300px',
                    color: '#999',
                    textAlign: 'center',
                    px: 4
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>
                    No import jobs yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Click "Publish..." to create your first job
                </Typography>
            </Box>
        );
    }

    // All caught up state
    if (!loading && !hasActiveJobs && !hasFailedJobs && !hasRecentJobs) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '300px',
                    color: '#999',
                    textAlign: 'center',
                    px: 4
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>
                    All caught up! 🎉
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    No active or recent jobs
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 2 }}>
            {/* Summary Stats */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 4,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    mb: 5
                }}
            >
                {hasActiveJobs && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#1e88e5'
                            }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activeJobs.length} Active
                        </Typography>
                    </Box>
                )}
                {hasFailedJobs && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#E53935'
                            }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {failedJobs.length} Failed
                        </Typography>
                    </Box>
                )}
                {completedJobs.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#43a047'
                            }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {completedJobs.length} Completed
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Active Jobs Section */}
            {hasActiveJobs && (
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                            cursor: 'pointer'
                        }}
                        onClick={() => setActiveExpanded(!activeExpanded)}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                fontWeight: 600,
                                color: '#2196f3',
                                letterSpacing: '0.5px',
                                fontSize: '14px'
                            }}
                        >
                            ACTIVE JOBS ({activeJobs.length})
                        </Typography>
                        <IconButton size="small">
                            {activeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={activeExpanded}>
                        {activeJobs.map(job => (
                            <ImportJobCard
                                key={job.jobId}
                                job={job}
                                onCancel={onCancelJob}
                                policySetMap={policySetMap}
                            />
                        ))}
                    </Collapse>
                </Box>
            )}

            {/* Failed Jobs Section */}
            {hasFailedJobs && (
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                            cursor: 'pointer'
                        }}
                        onClick={() => setNeedsAttentionExpanded(!needsAttentionExpanded)}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                fontWeight: 600,
                                color: '#f44336',
                                letterSpacing: '0.5px',
                                fontSize: '14px'
                            }}
                        >
                            NEEDS ATTENTION ({failedJobs.length})
                        </Typography>
                        <IconButton size="small">
                            {needsAttentionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={needsAttentionExpanded}>
                        {failedJobs.map(job => (
                            <ImportJobCard
                                key={job.jobId}
                                job={job}
                                policySetMap={policySetMap}
                            />
                        ))}
                    </Collapse>
                </Box>
            )}

            {/* Recent Completions Section */}
            {hasRecentJobs && (
                <Box sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                            cursor: 'pointer'
                        }}
                        onClick={() => setRecentExpanded(!recentExpanded)}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                fontWeight: 600,
                                color: '#43a047',
                                letterSpacing: '0.5px',
                                fontSize: '14px'
                            }}
                        >
                            RECENT ({recentCompletedJobs.length})
                        </Typography>
                        <IconButton size="small">
                            {recentExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={recentExpanded}>
                        <Box
                            sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                p: 2,
                                backgroundColor: '#fff'
                            }}
                        >
                            {recentCompletedJobs.map(job => (
                                <ImportJobCard key={job.jobId} job={job} policySetMap={policySetMap} />
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
}
